import { useGetOrderPricingMode } from '@src/hooks/useGetOrderPricingMode';
import { useStrategies } from '@src/providers';
import { OrgConfigurationKey, useOrgConfiguration } from '@src/providers/OrgConfigurationProvider';
import { OrderStrategiesEnum } from '@src/tokens/orderStrategy';
import {
  AggressorFlagEnum,
  TimeInForceEnum,
  UnifiedLiquidityEnum,
  format,
  isMultileg,
  useMarketAccountsContext,
  type Allocation,
  type OrdTypeEnum,
  type OrderIntent,
  type Security,
  type SideEnum,
  type SubAccount,
} from '@talos/kyoko';
import { uniq } from 'lodash-es';
import { useCallback } from 'react';
import { v1 as uuid } from 'uuid';
import { MULTILEG_MARKET_ACCOUNT } from '../../providers/OMSContext.types';
import type { IOrderPreset } from '../../providers/OrderPresetsContext';
import type { SendOrderArgs } from '../../providers/orders.types';
import type { PrimeOMSParams } from '../OMS/NewOrder/types';

interface UsePrepareOrderProps {
  security: Security | undefined;
  selectedMarketAccounts: string[];
  selectedMarkets: string[] | undefined;
  unifiedLiquidity: UnifiedLiquidityEnum | undefined;
  selectedAggregation?: string;
}

export const usePrepareOrder = ({
  security,
  selectedMarketAccounts,
  selectedMarkets,
  unifiedLiquidity,
  selectedAggregation,
}: UsePrepareOrderProps) => {
  const { getConfig } = useOrgConfiguration();
  const { getOrderPricingMode } = useGetOrderPricingMode(security);
  const { strategiesList, strategiesByName } = useStrategies();
  const { marketAccountsByMarket } = useMarketAccountsContext();

  /**
   * ordType - optional here as it is only provided when using click to trade. When regularly priming from a price click
   * we do not want to send the ordType as it might be inferred by the strategy.
   */
  const prepareOrder = useCallback(
    ({
      side,
      orderQty,
      orderCurrency,
      price,
      strategyName,
      ordType,
      expectedFillPrice,
      expectedFillQty,
      orderPreset,
      intent,
      defaultSubAccount,
      marketCardSubAccount,
    }: {
      side: SideEnum;
      orderQty: string;
      orderCurrency: string;
      price: string;
      strategyName: string;
      ordType?: OrdTypeEnum;
      expectedFillPrice?: string;
      expectedFillQty?: string;
      orderPreset?: IOrderPreset;
      intent?: OrderIntent;
      defaultSubAccount?: SubAccount | undefined;
      marketCardSubAccount?: SubAccount | undefined; // This overwrite all other subaccount settings (defaultSubAccount or orderPreset.allocations)
    }) => {
      if (security == null) {
        return null;
      }

      const getSelectedStrategy = () => {
        if (!isMultileg(security)) {
          // return strategy matching selection, or default to Limit
          const selectedStrategy = strategiesByName.get(strategyName);
          if (unifiedLiquidity === UnifiedLiquidityEnum.Enabled) {
            // If unified liquidity is enabled, we need to check if the selected strategy supports it
            return selectedStrategy && selectedStrategy.supportsUnifiedLiquidity
              ? selectedStrategy
              : strategiesByName.get(OrderStrategiesEnum.Limit);
          } else {
            return selectedStrategy ?? strategiesByName.get(OrderStrategiesEnum.Limit);
          }
        }

        if (isMultileg(security)) {
          // Need to select a strategy acceptable by multileg
          const multilegStrategies = strategiesList.filter(
            strategy => strategy.InstrumentScope === 'Synthetic' || strategy.InstrumentScope === 'All'
          );
          const mlStrategiesByName = new Map(multilegStrategies.map(s => [s.Name, s]));
          const selectedStrategy = mlStrategiesByName.get(strategyName);
          if (unifiedLiquidity === UnifiedLiquidityEnum.Enabled) {
            return selectedStrategy && selectedStrategy.supportsUnifiedLiquidity
              ? selectedStrategy
              : // Use Limit as fallback when UL is enabled
                strategiesByName.get(OrderStrategiesEnum.Limit);
          }
          // Use MultilegAggressiveLimit as fallback when UL is disabled
          const fallbackStrategy = mlStrategiesByName.get(OrderStrategiesEnum.MultilegAggressiveLimit);
          return selectedStrategy ?? fallbackStrategy ?? multilegStrategies.at(0);
        }
      };

      const selectedStrategy = getSelectedStrategy();
      const marketAccounts = selectedMarketAccounts.slice();

      // In the MarketDataCard for multilegs, we are currently putting both the multileg/multileg market account,
      // AND any UL Leg market accounts into the selectedMarketAccounts array. So this is how we get the ulLegMarkets back out
      // This works because for multileg, we can currently _only_ select UL leg markets (and multileg/multileg is permanently selected)
      const ulLegMarkets = isMultileg(security)
        ? marketAccounts.filter(ma => ma !== MULTILEG_MARKET_ACCOUNT)
        : undefined;

      // This config parameter lets us override the time in force on Aggressive click to trade orders. This lets them
      // be FOK instead of just GTC.
      let timeInForce = getConfig(
        OrgConfigurationKey.ClickToTradeTimeInForce,
        TimeInForceEnum.GoodTillCancel
      ) as TimeInForceEnum;

      // Passive orders should always be GTC no matter the config
      if (intent === AggressorFlagEnum.Passive) {
        timeInForce = TimeInForceEnum.GoodTillCancel;
      }
      // If old config includes markets with existing market accounts, add them
      if (selectedMarkets != null) {
        const accountsFromMarkets = selectedMarkets.reduce(
          (res: string[], market) =>
            marketAccountsByMarket.has(market) ? [...res, marketAccountsByMarket.get(market)![0].Name] : res,
          []
        );
        marketAccounts.push(...accountsFromMarkets);
      }

      const allocations = marketCardSubAccount
        ? [{ subAccount: marketCardSubAccount.Name, value: '1' } satisfies Allocation]
        : (orderPreset?.allocations ??
          (defaultSubAccount?.Name
            ? [{ subAccount: defaultSubAccount.Name, value: '1' } satisfies Allocation]
            : undefined));

      return {
        symbol: security.Symbol,
        orderCurrency,
        side,
        ordType: ordType,
        orderQty: format(orderQty, {
          spec: security.DefaultSizeIncrement,
          pretty: false,
          removeTrailingZeros: true,
        }),
        price,
        pricingMode: getOrderPricingMode(),
        orderPreset: orderPreset ? { ...orderPreset, allocations: allocations ?? [] } : undefined,
        subAccountAllocations: allocations,
        marketAccounts: isMultileg(security) ? [MULTILEG_MARKET_ACCOUNT] : uniq(marketAccounts),
        timeInForce,
        selectedStrategy: selectedStrategy!,
        expectedFillPrice,
        expectedFillQty,
        parameters: {
          unifiedLiquidity: unifiedLiquidity ?? UnifiedLiquidityEnum.Disabled,
        },
        clOrdID: uuid(),
        group: '',
        ulLegMarkets,
        aggregation: selectedAggregation,
        primeFrom: 'MarketCard',
      } satisfies SendOrderArgs | PrimeOMSParams;
    },
    [
      security,
      strategiesList,
      strategiesByName,
      selectedMarketAccounts,
      getConfig,
      selectedMarkets,
      unifiedLiquidity,
      marketAccountsByMarket,
      selectedAggregation,
      getOrderPricingMode,
    ]
  );

  return prepareOrder;
};
